import { type ReactNode, useState } from "react";

import { changeCarrierInstructionCostSelection } from "~/modules/orders/order-documents/pickup-instructions/carrier-rate-selection";
import {
  type CarrierInstructionCostCandidate,
  type CarrierInstructionCostReference,
  carrierInstructionCostKey,
  formatCarrierInstructionCostAmount,
} from "~/shared/documents/carrier-instruction-costs";
import { Button, Checkbox, Collapse, Group, Stack, Table, Text } from "~/ui";

type CarrierRateCostPickerProps = {
  candidates: CarrierInstructionCostCandidate[];
  value: CarrierInstructionCostReference[];
  onChange: (value: CarrierInstructionCostReference[]) => void;
  error?: ReactNode;
};

const costSourceLabel = (cost: CarrierInstructionCostCandidate): string => `${cost.sourceType === "order" ? "SO" : "PO"} ${cost.orderNo}`;

export const CarrierRateCostPicker = ({ candidates, value, onChange, error }: CarrierRateCostPickerProps) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const selectedKeys = new Set(value.map(carrierInstructionCostKey));
  const candidatesByKey = new Map(candidates.map(cost => [carrierInstructionCostKey(cost), cost]));
  const selectedCosts = value.flatMap(reference => {
    const candidate = candidatesByKey.get(carrierInstructionCostKey(reference));
    return candidate ? [candidate] : [];
  });
  const hasUnavailableSelection = value.some(cost => !candidatesByKey.has(carrierInstructionCostKey(cost)));

  return (
    <Stack spacing="xs">
      <Text size="sm" color="dimmed">
        Costs categorized as freight are selected automatically. Choose which costs to include on this document.
      </Text>
      {value.length === 0 && <Text size="sm">No costs selected. The rate confirmation section will not appear on the PDF.</Text>}
      {selectedCosts.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Source</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {selectedCosts.map(cost => (
              <tr key={carrierInstructionCostKey(cost)}>
                <td>{cost.description || "Cost without a description"}</td>
                <td>
                  {costSourceLabel(cost)}
                  {cost.supplierName && <Text size="xs">{cost.supplierName}</Text>}
                </td>
                <td>
                  {formatCarrierInstructionCostAmount(cost.amountCents)} {cost.currencyCode}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {hasUnavailableSelection && (
        <Text size="sm" color="red">
          A selected cost is no longer available. Clear the selection and choose costs again.
        </Text>
      )}
      {error && (
        <Text size="sm" color="red" role="alert">
          {error}
        </Text>
      )}
      <Group>
        <Button type="button" variant="subtle" compact onClick={() => setPickerOpen(open => !open)} aria-expanded={pickerOpen}>
          {pickerOpen ? "Done choosing costs" : "Change costs"}
        </Button>
        {value.length > 0 && (
          <Button type="button" variant="subtle" compact onClick={() => onChange([])}>
            Clear all
          </Button>
        )}
      </Group>
      <Collapse in={pickerOpen}>
        {candidates.length === 0 ? (
          <Text size="sm" color="dimmed">
            No saved costs are available on this order or its linked order.
          </Text>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Include</th>
                <th>Cost</th>
                <th>Source / supplier</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map(cost => (
                <tr key={carrierInstructionCostKey(cost)}>
                  <td>
                    <Checkbox
                      aria-label={`Include ${cost.description || "cost"} from ${costSourceLabel(cost)}`}
                      checked={selectedKeys.has(carrierInstructionCostKey(cost))}
                      onChange={event => onChange(changeCarrierInstructionCostSelection(value, cost, event.currentTarget.checked))}
                    />
                  </td>
                  <td>
                    {cost.description || "Cost without a description"}
                    {cost.accountName && (
                      <Text size="xs" color="dimmed">
                        {cost.accountName}
                      </Text>
                    )}
                  </td>
                  <td>
                    {costSourceLabel(cost)}
                    {cost.supplierName && <Text size="xs">{cost.supplierName}</Text>}
                  </td>
                  <td>
                    {formatCarrierInstructionCostAmount(cost.amountCents)} {cost.currencyCode}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Collapse>
    </Stack>
  );
};
